'use client';

import { IconCheck, IconPlus, IconReceipt2 } from '@tabler/icons-react';
import React from 'react';
import { motion } from 'framer-motion';

import { Container } from '../container';
import { Heading } from '../elements/heading';
import { Subheading } from '../elements/subheading';
import { PageBreadcrumb } from '../page-breadcrumb';
import { PillButton } from '../pill-button';
import { jakarta } from '@/lib/fonts';
import { cn } from '@/lib/utils';

type Perks = {
  [key: string]: string;
};

type CTA = {
  [key: string]: string;
};

type Plan = {
  name: string;
  price?: number | null;
  sub_text?: string | null;
  perks: Perks[];
  additional_perks?: Perks[];
  description?: string;
  number?: string;
  featured?: boolean;
  CTA?: CTA | undefined;
};

export const Pricing = ({
  heading,
  sub_heading,
  plans,
  locale = 'en',
}: {
  heading: string;
  sub_heading: string;
  plans: any[];
  locale?: string;
}) => {
  const onClick = (plan: Plan) => {
    console.log('click', plan);
  };

  return (
    <section className="relative overflow-hidden pt-28 md:pt-36 pb-20 md:pb-28 bg-[#f4f2fc] dark:bg-neutral-900/90 border-b border-[#e5e2f5] dark:border-neutral-800/80">
      {/* Discreet background ambient lighting */}
      <div className="absolute inset-0 pointer-events-none -z-10 overflow-hidden">
        <div className="absolute top-1/3 right-0 w-[550px] h-[550px] rounded-full blur-[150px] opacity-[0.04] bg-[#c02029]" />
        <div className="absolute bottom-1/4 left-0 w-[500px] h-[500px] rounded-full blur-[150px] opacity-[0.035] bg-[#c02029]" />
      </div>

      <Container>
        {/* Streamlined Breadcrumb Header */}
        <div className="flex justify-center">
          <PageBreadcrumb
            locale={locale}
            center
            items={[{ label: locale === 'fr' ? 'Tarifs' : 'Pricing' }]}
          />
        </div>

        <Heading className="pt-2">{heading}</Heading>
        <Subheading className="max-w-3xl mx-auto">{sub_heading}</Subheading>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 max-w-7xl mx-auto gap-6 sm:gap-8 py-16 lg:items-stretch">
          {plans.map((plan, idx) => (
            <Card
              onClick={() => onClick(plan)}
              key={plan.name || idx}
              plan={plan}
              locale={locale}
            />
          ))}
        </div>
      </Container>
    </section>
  );
};

const Card = ({
  plan,
  onClick,
  locale = 'en',
}: {
  plan: Plan;
  onClick: () => void;
  locale?: string;
}) => {
  const isFeatured = !!plan.featured;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={cn(
        'flex flex-col justify-between p-6 sm:p-8 rounded-3xl transition-all duration-300 relative group',
        'bg-white dark:bg-neutral-900',
        'border shadow-xl',
        isFeatured
          ? 'border-[#c02029]/60 shadow-2xl shadow-[#c02029]/10 ring-2 ring-[#c02029]/20'
          : 'border-[#e5e2f5] dark:border-neutral-800 shadow-[#301754]/5 hover:border-[#c02029]/30'
      )}
    >
      {/* Featured Badge */}
      {isFeatured && (
        <div className="absolute -top-3.5 right-6 bg-[#c02029] text-white text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full shadow-md shadow-[#c02029]/30">
          {locale === 'fr' ? 'Populaire' : 'Featured'}
        </div>
      )}

      {/* Top Section */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3
            className={cn(
              jakarta.className,
              'text-xl font-bold text-[#2e1452] dark:text-white'
            )}
          >
            {plan.name}
          </h3>
        </div>

        {plan.description && (
          <p className="text-sm text-[#5e5873] dark:text-neutral-400 mb-6 leading-relaxed">
            {plan.description}
          </p>
        )}

        <div className="mt-4 mb-8 flex items-baseline flex-wrap gap-x-1.5">
          {plan.price ? (
            <>
              <span
                className={cn(
                  jakarta.className,
                  'text-4xl sm:text-5xl font-extrabold text-[#2e1452] dark:text-white'
                )}
              >
                {new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US').format(plan.price)}
              </span>
              <span className="text-xl font-bold text-[#c02029]">€</span>
              <span className="text-sm font-medium text-[#5e5873] dark:text-neutral-400 ml-1">
                {plan.sub_text || (locale === 'fr' ? '/ projet' : '/ project')}
              </span>
            </>
          ) : (
            <span
              className={cn(
                jakarta.className,
                'text-2xl sm:text-3xl font-extrabold text-[#2e1452] dark:text-white'
              )}
            >
              {plan.sub_text || (locale === 'fr' ? 'Sur devis' : 'Custom quote')}
            </span>
          )}
        </div>

        {/* CTA Button */}
        <div className="mb-8">
          <PillButton
            href={plan?.CTA?.URL || `/${locale}/contact`}
            onClick={onClick}
            className="w-full justify-between"
            text={plan?.CTA?.text || (locale === 'fr' ? 'Choisir cette formule' : 'Get Started')}
          />
        </div>

        {/* Perks list */}
        <div className="space-y-3 pt-4 border-t border-[#e5e2f5] dark:border-neutral-800">
          <p className="text-xs font-semibold text-[#50456c] dark:text-neutral-400 uppercase tracking-wider mb-2">
            {locale === 'fr' ? 'Inclus dans la formule :' : 'What is included:'}
          </p>
          {plan.perks?.map((feature, idx) => (
            <Step featured={isFeatured} key={idx}>
              {feature.text}
            </Step>
          ))}
        </div>

        {plan.additional_perks && plan.additional_perks.length > 0 && (
          <>
            <div className="my-6 border-t border-dashed border-[#e5e2f5] dark:border-neutral-800" />
            <div className="space-y-3">
              <p className="text-xs font-semibold text-[#c02029] uppercase tracking-wider mb-2">
                {locale === 'fr' ? 'En bonus :' : 'Bonus perks:'}
              </p>
              {plan.additional_perks?.map((feature, idx) => (
                <Step featured={isFeatured} additional key={idx}>
                  {feature.text}
                </Step>
              ))}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

const Step = ({
  children,
  additional,
  featured,
}: {
  children: React.ReactNode;
  additional?: boolean;
  featured?: boolean;
}) => {
  return (
    <div className="flex items-start gap-3">
      <span
        className={cn(
          'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs',
          additional
            ? 'bg-[#c02029] text-white shadow-sm shadow-[#c02029]/30'
            : 'bg-[#c02029]/10 text-[#c02029]'
        )}
      >
        <IconCheck className="w-3.5 h-3.5 stroke-[2.75]" />
      </span>
      <span className="text-sm font-medium text-[#5e5873] dark:text-neutral-200 leading-snug">
        {children}
      </span>
    </div>
  );
};

export default Pricing;
